<template>
  <div>
    <page-header :title="title" :container-class="containerClass" :breadcrumb="breadcrumb" />

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search..." v-model="searchTerm" @input="onSearch" />

                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-lg-6">
                <date-picker
                  v-model="dateRange"
                  format="MMMM DD, YYYY"
                  valueType="YYYY-MM-DD"
                  style="width: 80%;"
                  id="date"
                  lang="en"
                  placeholder="Filter by Waitlist Date - End Date Range"
                  range
                  class="w-100"
                  :class="isMobileScreen ? 'mb-3' : ''"
                  :input-attr="{ required: 'true' }"
                ></date-picker>
                <b-form-invalid-feedback class="" :state="isDateEmpty ? false : null"
                  >This field is required.</b-form-invalid-feedback
                >
                <b-form-invalid-feedback class="mt-0 mb-2" :state="dateLimitExceeded ? false : null"
                  >Please select a date range of one year or less.</b-form-invalid-feedback
                >
              </div>
              <div class="col-lg-6" style="display: flex; align-self: center; justify-content: end;">
                <b-btn @click="exportWaitlist" variant="primary" :disabled="isExportLoading">Export Waitlist</b-btn>
              </div>
            </div>
          </div>

          <!-- Schools Table -->
          <b-table
            :fields="tableColumns"
            :items="allWaitListEntries"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap sticky-lcol-table"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <template #cell(menu)="data">
              <div class="text-center">
                <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                  <template #button-content>
                    <i class="material-icons icon-24pt card-course__icon-favorite text-info">more_vert</i>
                    <span class="sr-only"></span>
                  </template>

                  <b-dropdown-item :href="`mailto:${data.item.email}`">
                    <md-icon class="text-info">email</md-icon> Email
                  </b-dropdown-item>

                  <b-dropdown-item :href="`tel:${data.item.phone}`">
                    <md-icon class="text-info">phone</md-icon> Call
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>

            <template #cell(first_name)="data"> {{ data.item.first_name }} {{ data.item.last_name }}</template>

            <template #cell(email)="data"> {{ data.item.email }}</template>
            <template #cell(phone)="data"> {{ data.item.phone }}</template>
            <template #cell(date)="data"> {{ formatFullDate(data.item.created_at) }} </template>

            <template #cell(status)="data">
              <b-badge
                :class="
                  'border border-' + (data.item.status === 'informed' ? `success badge-success` : `danger badge-danger`)
                "
                variant="none"
              >
                {{ $t(`waitlistStatuses.${data.item.status}`) }}
              </b-badge>
            </template>
            <template #cell(informed)="data">
              {{ data.item.informed ? formatFullDateTime(data.item.informed) : '' }}
            </template>
            <template #cell(program)="data"> {{ data.item.program.title }}</template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalEntries"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="info-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce } from 'lodash';
import { mapActions } from 'vuex';
import { formatFullDate, formatFullDateTime } from '@/common/utils';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import DatePicker from 'vue2-datepicker';

export default {
  components: { MdIcon, PageHeader, Pagination, DatePicker },
  extends: Page,

  data() {
    return {
      title: 'Program Waitlist',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalEntries: 0,
      searchTerm: '',
      isExportLoading: false,
      allWaitListEntries: [],
      isFileDownloading: false,
      formatFullDate,
      formatFullDateTime,
      dateRange: [],
      isDateEmpty: false,
      dateLimitExceeded: false,
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Programs', to: { name: 'manage-programs' } },
        ...(this.$route.params.name ? [{ text: this.$route.params.name, active: true }] : []),
        { text: 'WaitList', active: true },
      ];
    },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },

    tableColumns() {
      return [
        {
          key: 'menu',
          label: '',
          tdClass: 'text-left',
        },
        { key: 'first_name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'program', label: 'Program' },
        { key: 'date', label: 'Waitlisted' },
        { key: 'status', label: 'Status' },
        { key: 'informed', label: 'Informed On' },
      ];
    },
  },
  watch: {
    dateRange() {
      this.fetchWaitList();
    },
  },
  methods: {
    ...mapActions('program', ['getWaitlist', 'getWaitlistRoster']),
    ...mapActions('fileDownload', ['downloadFile']),

    async fetchWaitList(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();
      const response = await this.getWaitlist({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        program_id: this.$route.query.program,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...(this.dateRange[0] && { date_from: this.dateRange[0] }),
        ...(this.dateRange[1] && { date_to: this.dateRange[1] }),
        ...params,
      });
      this.allWaitListEntries = response.data.results;
      this.currentPage = pageNum;
      this.totalEntries = response.data.count;
      this.isLoading = false;
    },
    async downloadCSV(url) {
      try {
        await this.downloadFile({
          fileUrl: url,
        });
        const h = this.$createElement;
        this.makeToast({
          variant: 'success',
          msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
          toaster: 'b-toaster-bottom-left',
        });
      } catch (error) {
        //
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    validateDate() {
      if (!this.dateRange[0] || !this.dateRange[1]) {
        this.isDateEmpty = true;
        this.dateLimitExceeded = false;
        this.makeToast({ variant: 'danger', msg: 'Please select specific date range' });
        return false;
      } else {
        this.isDateEmpty = false;
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        const sixMonthsInMs = (365 * 24 * 60 * 60 * 1000) / 2; // 6 months in milliseconds
        const diff = endDate - startDate;

        if (diff > sixMonthsInMs) {
          this.makeToast({ variant: 'danger', msg: 'Date range must be 6 months or less' });
          this.dateLimitExceeded = true;
          return false;
        } else {
          return true;
        }
      }
    },
    async exportWaitlist() {
      if (!this.validateDate()) {
        return;
      }
      this.isDateEmpty = false;
      this.dateLimitExceeded = false;
      this.isExportLoading = true;
      try {
        const response = await this.getWaitlistRoster({
          prog_id: this.$route.query.program,
          ...(this.dateRange[0] && { date_from: this.dateRange[0] }),
          ...(this.dateRange[1] && { date_to: this.dateRange[1] }),
        });
        this.makeToast({
          variant: 'success',
          msg: 'Downloading File',
        });
        this.downloadCSV(response.data.file_url);
      } catch (ex) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isExportLoading = false;
    },
    onPageChange(pageNum) {
      this.fetchWaitList(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchWaitList();
    },

    onSearch() {
      this.debouncedSearchWaitList(this);
    },

    debouncedSearchWaitList: debounce(vm => {
      vm.fetchWaitList();
    }, 500),
  },

  async mounted() {
    this.fetchWaitList();
  },
};
</script>
